import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { currentReturnPath } from '@sbe-spinoff-client/staff-http';
import { StaffAuthService } from './staff-auth.service';

/**
 * 자체 로그인 세션(쿠키) 가드.
 *
 * - `GET /staff/auth/me` 로 세션 유효성 + 초대 상태를 함께 확인한다.
 *   - `invitationStatus === 'Accepted'` → 라우트 진입 허용(단, `requireScopes` 분기 — 아래 참고).
 *   - `invitationStatus === 'Pending'` → 초대 수락/거절 블로킹 페이지(`/invitation`)로.
 *   - 그 외(Rejected/Cancelled/Expired) → welcome(`/welcome`)로 — 더 이상 staff 접근 권한 없음.
 * - 401(세션 없음/만료) → `/login`. 복귀할 경로를 `return` 쿼리에 실어 보낸다.
 *
 * `route.data['requireScopes']`가 true인 라우트(내부 셸)는 `hasEffectiveScopes === false`인
 * Accepted staff를 `/invitation-accepted`로 계속 되돌린다 — InvitationComponent#accept()의
 * 최초 1회 navigate만으로는 보호되지 않는다(북마크·뒤로가기·직접 URL 입력으로 우회 가능,
 * 2026-08-11 code-review로 발견). `/oauth/authorize`(requiresStaff OIDC RP interaction 복귀,
 * P0)는 이 플래그를 켜지 않는다 — 그 흐름에서는 권한 판단이 RP 자신의 몫이라 scope 0이어도
 * interaction을 막지 않는다(기존 설계 결정 유지).
 *
 * `route.data['preventIfHasScopes']`는 그 반대 방향 — `/invitation-accepted` 자신에 걸어,
 * 이미 권한이 부여된 staff가 북마크·뒤로가기로 이 안내 화면에 재진입하면 홈으로 돌려보낸다
 * (2026-08-11 Gemini 리뷰로 발견).
 */
export const staffAuthGuard: CanActivateFn = async (route) => {
  const auth = inject(StaffAuthService);
  const router = inject(Router);

  try {
    const me = await auth.me();
    if (me.invitationStatus === 'Accepted') {
      if (route.data['requireScopes'] && !me.hasEffectiveScopes) {
        return router.createUrlTree(['/invitation-accepted']);
      }
      if (route.data['preventIfHasScopes'] && me.hasEffectiveScopes) {
        return router.createUrlTree(['/']);
      }
      return true;
    }
    // Pending/그 외(Rejected·Cancelled·Expired) 분기도 unauth 분기와 동일하게 return 경로를
    // 실어 보낸다 — 특히 requiresStaff OIDC RP의 `/oauth/authorize?interaction=<uid>`에서 온
    // 진입이면(P0, oauth-authorize.component.ts) 이 쿼리 파라미터가 없으면 초대 수락 후에도
    // 원래 interaction으로 복귀할 방법이 없어 RP 로그인이 그대로 멈춘다(2026-08-11 code-review로
    // 발견). InvitationComponent#accept()가 이 값을 읽어 복귀한다.
    if (me.invitationStatus === 'Pending') {
      return router.createUrlTree(['/invitation'], { queryParams: { return: currentReturnPath() } });
    }
    return router.createUrlTree(['/welcome'], { queryParams: { return: currentReturnPath() } });
  } catch {
    return router.createUrlTree(['/login'], { queryParams: { return: currentReturnPath() } });
  }
};
